// Phase 44.01 — Admin Retry Queue
// Visualizza stato scheduler retry notifiche + coda (pending/exhausted/etc.)
// con azioni operative: force retry, cancel, run now.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, PlayCircle, ZapOff, Zap, AlertTriangle, CheckCircle2,
  Clock, Mail, Smartphone, MessageCircle, AlertOctagon, Trash2, Eye, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STATUS_META = {
  pending:   { label: "In coda",     cls: "bg-amber-50 text-amber-800 border-amber-200",  Icon: Clock },
  succeeded: { label: "Riuscita",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  exhausted: { label: "Dead-letter", cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertOctagon },
  cancelled: { label: "Annullata",   cls: "bg-zinc-100 text-zinc-700 border-zinc-200", Icon: ZapOff },
};

const CHANNEL_META = {
  email:    { label: "Email",    Icon: Mail },
  push:     { label: "Push",     Icon: Smartphone },
  whatsapp: { label: "WhatsApp", Icon: MessageCircle },
};

const POLL_MS = 15_000;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const now = new Date();
    const diffSec = Math.round((d - now) / 1000);
    const abs = Math.abs(diffSec);
    const fmt = d.toLocaleString("it-CH", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
    if (abs < 60) return `${fmt} · ora`;
    if (abs < 3600) return `${fmt} · ${diffSec > 0 ? "tra" : "−"}${Math.round(abs/60)} min`;
    return fmt;
  } catch { return iso; }
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}
      data-testid={`retry-status-pill-${status}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function ChannelPill({ channel }) {
  const meta = CHANNEL_META[channel] || { label: channel || "—", Icon: AlertTriangle };
  const Icon = meta.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-dapnow-ink">
      <Icon className="h-3.5 w-3.5 text-dapnow-blue" />
      {meta.label}
    </span>
  );
}

function StatCard({ label, value, accent = "blue", testid }) {
  const accents = {
    blue:    "border-dapnow-blue/30 bg-dapnow-blue-soft/60",
    amber:   "border-amber-300 bg-amber-50",
    emerald: "border-emerald-300 bg-emerald-50",
    red:     "border-red-300 bg-red-50",
    zinc:    "border-zinc-300 bg-zinc-50",
  };
  return (
    <div className={`rounded-2xl border ${accents[accent]} p-4`} data-testid={testid}>
      <p className="overline text-[10px] font-bold text-dapnow-muted">{label}</p>
      <p className="font-display text-3xl font-extrabold tracking-tight mt-1">{value ?? 0}</p>
    </div>
  );
}

function SchedulerCard({ status, onRunNow, running }) {
  const s = status?.scheduler || {};
  const ok = s.running && s.enabled;
  const stateLabel = !s.enabled ? "Disattivo (env)"
    : s.running ? "Attivo" : "Non avviato";
  const stateCls = !s.enabled ? "text-zinc-700 bg-zinc-100"
    : s.running ? "text-emerald-700 bg-emerald-100" : "text-amber-800 bg-amber-100";
  return (
    <section
      className="rounded-2xl border border-dapnow-line bg-white shadow-sm p-5"
      data-testid="retry-scheduler-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline text-[10px] font-bold text-dapnow-muted">Scheduler retry</p>
          <h2 className="font-display text-lg font-bold tracking-tight mt-0.5">
            Notifiche fallite — coda automatica
          </h2>
          <p className="text-xs text-dapnow-muted mt-1 leading-snug">
            Backoff esponenziale {Array.isArray(s.backoff_seconds) ? `(${s.backoff_seconds.join("s, ")}s)` : ""} · tick {s.tick_seconds || 60}s · max {s.max_attempts || 6} tentativi.
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${stateCls}`} data-testid="retry-scheduler-state">
          <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-emerald-600 animate-pulse" : "bg-zinc-500"}`} />
          {stateLabel}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <dt className="text-dapnow-muted">Prossimo tick</dt>
          <dd className="font-mono text-dapnow-ink mt-0.5" data-testid="retry-next-run">{fmtTs(s.next_run_at)}</dd>
        </div>
        <div>
          <dt className="text-dapnow-muted">Ultimo run</dt>
          <dd className="font-mono text-dapnow-ink mt-0.5" data-testid="retry-last-run">{fmtTs(s.last_run_at)}</dd>
        </div>
        <div>
          <dt className="text-dapnow-muted">Ultimo esito</dt>
          <dd className="text-dapnow-ink mt-0.5 truncate">
            {s.last_run_result?.error
              ? <span className="text-red-700">{s.last_run_result.error}</span>
              : s.last_run_result
                ? `ok=${s.last_run_result.succeeded ?? 0} · fail=${s.last_run_result.failed ?? 0} · dead=${s.last_run_result.exhausted ?? 0}`
                : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-dapnow-muted">Batch / tentativi</dt>
          <dd className="text-dapnow-ink mt-0.5">{s.batch_size || 25} / {s.max_attempts || 6}</dd>
        </div>
      </dl>

      {s.last_error && s.last_error !== "disabled" && (
        <p className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 inline-flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> Ultimo errore scheduler: {s.last_error}
        </p>
      )}

      <button
        type="button"
        onClick={onRunNow}
        disabled={running || !s.enabled}
        data-testid="retry-run-now-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-dapnow-ink text-white px-4 h-10 text-sm font-semibold tracking-tight hover:bg-zinc-800 transition disabled:opacity-60"
      >
        {running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
        {running ? "Esecuzione…" : "Esegui tick adesso"}
      </button>
    </section>
  );
}

function DetailModal({ entry, onClose }) {
  if (!entry) return null;
  const hist = Array.isArray(entry.history) ? entry.history : [];
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="retry-detail-modal">
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-4 border-b border-dapnow-line flex items-start justify-between gap-3">
          <div>
            <p className="overline text-[10px] font-bold text-dapnow-muted">Retry entry</p>
            <h3 className="font-display text-lg font-bold tracking-tight font-mono">{entry.id}</h3>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <StatusPill status={entry.status} />
              <ChannelPill channel={entry.channel} />
              <span className="text-xs text-dapnow-muted">evento: <span className="font-mono text-dapnow-ink">{entry.event_type}</span></span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="retry-detail-close-btn"
            className="rounded-lg p-1.5 hover:bg-zinc-100 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="overflow-y-auto p-4 space-y-4 text-sm">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <dt className="overline text-[10px] text-dapnow-muted">Tentativo</dt>
              <dd className="font-semibold mt-0.5">{entry.attempt ?? 0} / {entry.max_attempts ?? 6}</dd>
            </div>
            <div>
              <dt className="overline text-[10px] text-dapnow-muted">Prossimo retry</dt>
              <dd className="font-mono mt-0.5">{fmtTs(entry.next_retry_at)}</dd>
            </div>
            <div>
              <dt className="overline text-[10px] text-dapnow-muted">Ultimo tentativo</dt>
              <dd className="font-mono mt-0.5">{fmtTs(entry.last_attempt_at)}</dd>
            </div>
            <div>
              <dt className="overline text-[10px] text-dapnow-muted">Creata</dt>
              <dd className="font-mono mt-0.5">{fmtTs(entry.created_at)}</dd>
            </div>
            {entry.user_snapshot?.email && (
              <div className="sm:col-span-2">
                <dt className="overline text-[10px] text-dapnow-muted">Destinatario</dt>
                <dd className="mt-0.5 break-all">{entry.user_snapshot.email}{entry.user_snapshot.name ? ` · ${entry.user_snapshot.name}` : ""}</dd>
              </div>
            )}
            {entry.last_error && (
              <div className="sm:col-span-2">
                <dt className="overline text-[10px] text-red-700">Ultimo errore</dt>
                <dd className="mt-0.5 text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 font-mono text-xs break-all whitespace-pre-wrap">{entry.last_error}</dd>
              </div>
            )}
          </dl>

          {hist.length > 0 && (
            <div>
              <p className="overline text-[10px] text-dapnow-muted mb-2">Storico tentativi</p>
              <ol className="space-y-2">
                {hist.map((h, i) => (
                  <li key={i} className="rounded-xl border border-dapnow-line bg-white p-3 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">Tentativo #{h.attempt}</span>
                      <span className={`uppercase font-bold tracking-wide ${h.status === "succeeded" ? "text-emerald-700" : "text-red-700"}`}>{h.status}</span>
                    </div>
                    <p className="font-mono text-dapnow-muted mt-1">{fmtTs(h.at)}</p>
                    {h.error && <p className="mt-1 text-red-700 font-mono break-all whitespace-pre-wrap">{h.error}</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <details className="rounded-xl border border-dapnow-line bg-zinc-50">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-dapnow-muted">Payload (JSON)</summary>
            <pre className="p-3 text-[11px] font-mono text-dapnow-ink whitespace-pre-wrap break-all max-h-72 overflow-auto" data-testid="retry-payload-json">
              {JSON.stringify(entry.payload ?? {}, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

export default function AdminRetryQueue() {
  const [status, setStatus] = useState(null);
  const [queue, setQueue] = useState({ total: 0, items: [], counts_by_status: {} });
  const [filterStatus, setFilterStatus] = useState("pending");
  const [filterChannel, setFilterChannel] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadAll = async () => {
    try {
      const [{ data: s }, { data: q }] = await Promise.all([
        api.get("/admin/retry/status"),
        api.get("/admin/retry/queue", {
          params: { status: filterStatus || undefined, channel: filterChannel || undefined, limit: 100 },
        }),
      ]);
      setStatus(s);
      setQueue(q);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterStatus, filterChannel]);

  useEffect(() => {
    const id = setInterval(loadAll, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [filterStatus, filterChannel]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/retry/run-now");
      toast.success(`Tick eseguito · processate ${data.processed ?? 0}, ok ${data.succeeded ?? 0}, dead ${data.exhausted ?? 0}`);
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const forceRetry = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/retry/${id}/force`);
      toast.success("Retry forzato — sarà ritentata al prossimo tick (entro 60s).");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const cancelRetry = async (id) => {
    if (!window.confirm("Confermi l'annullamento? L'entry non sarà più ritentata.")) return;
    setBusyId(id);
    try {
      await api.post(`/admin/retry/${id}/cancel`);
      toast.success("Entry annullata.");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const counts = status?.counts_by_status || queue.counts_by_status || {};
  const pendingByChannel = useMemo(() => status?.pending_by_channel || {}, [status]);

  return (
    <div className="space-y-5 overflow-x-hidden" data-testid="admin-retry-queue-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Admin · osservabilità</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Retry Queue</h1>
          <p className="text-sm text-dapnow-muted mt-1">
            Coda dei tentativi automatici per email, push e WhatsApp falliti.
          </p>
        </div>
        <button
          type="button"
          onClick={loadAll}
          className="inline-flex items-center gap-2 rounded-xl border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold hover:border-dapnow-ink transition"
          data-testid="retry-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Aggiorna
        </button>
      </header>

      <SchedulerCard status={status} onRunNow={runNow} running={running} />

      {/* Stat cards */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="retry-stats">
        <StatCard label="In coda" value={counts.pending ?? 0} accent="amber" testid="retry-stat-pending" />
        <StatCard label="Riuscite" value={counts.succeeded ?? 0} accent="emerald" testid="retry-stat-succeeded" />
        <StatCard label="Dead-letter" value={counts.exhausted ?? 0} accent="red" testid="retry-stat-exhausted" />
        <StatCard label="Annullate" value={counts.cancelled ?? 0} accent="zinc" testid="retry-stat-cancelled" />
      </section>

      {/* Pending per canale */}
      {Object.keys(pendingByChannel).length > 0 && (
        <section className="rounded-2xl border border-dapnow-line bg-white p-4" data-testid="retry-pending-by-channel">
          <p className="overline text-[10px] font-bold text-dapnow-muted">In coda per canale</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(pendingByChannel).map(([ch, n]) => {
              const meta = CHANNEL_META[ch] || { label: ch, Icon: AlertTriangle };
              const Icon = meta.Icon;
              return (
                <span key={ch} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 text-xs font-semibold">
                  <Icon className="h-3.5 w-3.5" />
                  {meta.label}: {n}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {/* Filtri */}
      <section className="flex items-center gap-2 flex-wrap" data-testid="retry-filters">
        <div className="inline-flex rounded-xl border border-dapnow-line bg-white p-1">
          {["pending", "exhausted", "succeeded", "cancelled", ""].map((s) => (
            <button
              key={s || "all"}
              type="button"
              onClick={() => setFilterStatus(s)}
              data-testid={`retry-filter-status-${s || "all"}`}
              className={`px-3 h-8 text-xs font-semibold rounded-lg transition ${filterStatus === s ? "bg-dapnow-ink text-white" : "text-dapnow-muted hover:text-dapnow-ink"}`}
            >
              {s ? STATUS_META[s].label : "Tutte"}
            </button>
          ))}
        </div>
        <select
          value={filterChannel}
          onChange={(e) => setFilterChannel(e.target.value)}
          data-testid="retry-filter-channel"
          className="h-9 rounded-xl border border-dapnow-line bg-white px-3 text-xs font-semibold"
        >
          <option value="">Tutti i canali</option>
          <option value="email">Email</option>
          <option value="push">Push</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
      </section>

      {/* Tabella */}
      <section className="rounded-2xl border border-dapnow-line bg-white overflow-hidden" data-testid="retry-table">
        {loading ? (
          <div className="p-8 text-center text-dapnow-muted text-sm">Caricamento…</div>
        ) : queue.items.length === 0 ? (
          <div className="p-8 text-center" data-testid="retry-empty-state">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600" />
            <p className="font-display text-base font-bold mt-2">Nessuna entry per i filtri attuali.</p>
            <p className="text-xs text-dapnow-muted mt-1">
              {filterStatus === "pending" ? "Ottime notizie: nessuna notifica in coda di retry." : "Cambia filtro per esplorare l'archivio."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-dapnow-line">
            {queue.items.map((it) => {
              const isPending = it.status === "pending";
              return (
                <li key={it.id} className="p-4 sm:p-5 flex items-start gap-3 flex-wrap" data-testid={`retry-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusPill status={it.status} />
                      <ChannelPill channel={it.channel} />
                      <span className="text-xs font-mono text-dapnow-muted truncate" title={it.event_type}>
                        {it.event_type}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm font-semibold text-dapnow-ink truncate">
                      {it.user_snapshot?.email || it.user_snapshot?.phone || "—"}
                    </p>
                    <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-dapnow-muted">
                      <span>tentativo <strong className="text-dapnow-ink">{it.attempt ?? 0}/{it.max_attempts ?? 6}</strong></span>
                      <span>prossimo: <span className="font-mono text-dapnow-ink">{fmtTs(it.next_retry_at)}</span></span>
                      {it.last_error && (
                        <span className="text-red-700 truncate max-w-[260px]" title={it.last_error}>
                          err: {it.last_error}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setDetail(it)}
                      data-testid={`retry-detail-btn-${it.id}`}
                      className="inline-flex items-center gap-1 rounded-lg border border-dapnow-line bg-white px-2.5 h-9 text-xs font-semibold hover:border-dapnow-ink transition"
                    >
                      <Eye className="h-3.5 w-3.5" /> Dettagli
                    </button>
                    {isPending && (
                      <>
                        <button
                          type="button"
                          onClick={() => forceRetry(it.id)}
                          disabled={busyId === it.id}
                          data-testid={`retry-force-btn-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-lg bg-dapnow-blue text-white px-2.5 h-9 text-xs font-semibold hover:bg-dapnow-blue/90 transition disabled:opacity-60"
                        >
                          <Zap className="h-3.5 w-3.5" /> Forza
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelRetry(it.id)}
                          disabled={busyId === it.id}
                          data-testid={`retry-cancel-btn-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 text-red-700 bg-white px-2.5 h-9 text-xs font-semibold hover:bg-red-50 transition disabled:opacity-60"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Annulla
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {queue.total > queue.items.length && (
          <div className="px-4 py-3 text-xs text-dapnow-muted border-t border-dapnow-line">
            Mostrate {queue.items.length} di {queue.total}. Affina i filtri per restringere.
          </div>
        )}
      </section>

      <DetailModal entry={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
